import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ComputerIcon from '@mui/icons-material/Computer';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ErrorIcon from '@mui/icons-material/Error';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip
} from '@mui/material';
import { useAuth } from '@qwickapps/auth-client';
import { Button, Code, GridCell, GridLayout, Text } from '@qwickapps/react-framework';
import { useMiddlewareClient, type SubscriptionInfo } from '@qwickforge/react-client';
import { useEffect, useState } from 'react';
import UpgradeNotice from '../components/UpgradeNotice';
import { getDeviceStatus } from '../components/DeviceStatus';

interface Device {
  id: string;
  name: string;
  type: string;
  hostname?: string;
  platform?: string;
  lastSeenAt?: string;
  createdAt: string;
  deletedAt?: string | null;
  apiKey?: string;
}

export default function DevicesPage() {
  const { session } = useAuth();
  const client = useMiddlewareClient();
  const [devices, setDevices] = useState<Device[]>([]);
  const [trashedDevices, setTrashedDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'trash'>('active');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [pairingCode, setPairingCode] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [isPolling, setIsPolling] = useState(false);
  const [deviceRegistered, setDeviceRegistered] = useState(false);
  const [codeExpired, setCodeExpired] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadDevices();
    loadSubscriptionInfo();
  }, [session]);

  useEffect(() => {
    if (registerDialogOpen && !pairingCode && !deviceRegistered) {
      generatePairingCode();
    }
  }, [registerDialogOpen]);

  useEffect(() => {
    if (registerDialogOpen && pairingCode && !deviceRegistered && !codeExpired) {
      setIsPolling(true);
      const interval = setInterval(async () => {
        const result = await checkDeviceRegistration(pairingCode);
        if (result.registered) {
          setDeviceRegistered(true);
          setIsPolling(false);
          clearInterval(interval);
          await loadDevices();
          await loadSubscriptionInfo();
        } else if (result.expired) {
          setCodeExpired(true);
          setIsPolling(false);
          setError('Pairing code has expired. Please generate a new code.');
          clearInterval(interval);
        }
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [registerDialogOpen, pairingCode, deviceRegistered, codeExpired]);

  const loadDevices = async () => {
    if (!session?.accessToken || !client) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Fetch active devices
      const activeData = await client.devices.list();
      const activeDevices = Array.isArray(activeData) ? activeData : activeData.devices || [];
      console.log('Active Devices:', activeDevices);
      // Fetch trashed devices
      const trashedData = await client.devices.list({ trashed: true });
      const trashed = Array.isArray(trashedData) ? trashedData : trashedData.devices || [];
      
      setDevices(activeDevices);
      setTrashedDevices(trashed);
    } catch (err) {
      setError('Failed to load devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionInfo = async () => {
    if (!client) return;

    try {
      const info = await client.subscription.getInfo();
      setSubscriptionInfo(info);
    } catch (err) {
      // Use default free plan on error
      setSubscriptionInfo({
        plan: 'free',
        status: 'active',
        currentProjects: 0,
        projectLimit: 1,
        canCreateMore: false,
        currentDevices: 0,
        deviceLimit: 2,
        canCreateMoreDevices: false,
        subscription: null,
      });
    }
  };

  const generatePairingCode = async () => {
    if (!session?.accessToken || !client) return;

    try {
      setError('');
      setCodeExpired(false);

      const data = await client.pairing.generateCode();
      setPairingCode(data.code);
      setExpiresAt(data.expires_at);
    } catch (error) {
      setError('Failed to generate pairing code. Please try again.');
    }
  };

  const checkDeviceRegistration = async (code: string): Promise<{ registered: boolean; expired?: boolean }> => {
    if (!client) return { registered: false };

    try {
      const data = await client.pairing.checkStatus(code);
      return {
        registered: data.registered === true,
        expired: data.expired === true,
      };
    } catch (error) {
      return { registered: false };
    }
  };

  const handleCloseRegisterDialog = () => {
    setRegisterDialogOpen(false);
    setPairingCode('');
    setExpiresAt('');
    setDeviceRegistered(false);
    setCodeExpired(false);
    setError('');
  };

  const handleRegenerateCode = () => {
    setPairingCode('');
    setCodeExpired(false);
    setError('');
    generatePairingCode();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleRevokeDevice = async () => {
    if (!selectedDevice || !session?.accessToken || !client) return;

    try {
      setSubmitting(true);
      setError('');

      await client.devices.delete(selectedDevice.id);

      setDevices(devices.filter(d => d.id !== selectedDevice.id));
      setTrashedDevices([...trashedDevices, { ...selectedDevice, deletedAt: new Date().toISOString() }]);
      setRevokeDialogOpen(false);
      setSelectedDevice(null);

      // Refresh subscription info
      await loadSubscriptionInfo();
    } catch (err) {
      setError('Failed to revoke device. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestoreDevice = async () => {
    if (!selectedDevice || !session?.accessToken || !client) return;

    try {
      setSubmitting(true);
      setError('');

      // Check if can create more devices before restoring
      if (subscriptionInfo && !subscriptionInfo.canCreateMoreDevices) {
        setError(`Cannot restore device. You've reached your limit (${subscriptionInfo.deviceLimit} devices on ${subscriptionInfo.plan.toUpperCase()} plan). Revoke an active device or upgrade to restore.`);
        return;
      }

      await client.devices.restore(selectedDevice.id);

      setTrashedDevices(trashedDevices.filter(d => d.id !== selectedDevice.id));
      setDevices([...devices, { ...selectedDevice, deletedAt: null }]);
      setRestoreDialogOpen(false);
      setSelectedDevice(null);

      // Refresh subscription info
      await loadSubscriptionInfo();
    } catch (err) {
      setError('Failed to restore device. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedDevice || !session?.accessToken || !client) return;

    try {
      setSubmitting(true);
      setError('');

      await client.devices.permanentDelete(selectedDevice.id);

      setTrashedDevices(trashedDevices.filter(d => d.id !== selectedDevice.id));
      setPermanentDeleteDialogOpen(false);
      setSelectedDevice(null);
    } catch (err) {
      setError('Failed to permanently delete device. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'free': return 'default';
      case 'pro': return 'primary';
      case 'enterprise': return 'secondary';
      default: return 'default';
    }
  };

  const getUsageColor = (current: number, limit: number): 'info' | 'warning' | 'error' => {
    const percentage = (current / limit) * 100;
    if (percentage >= 100) return 'error';
    if (percentage >= 50) return 'warning';
    return 'info';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Text variant="h4" gutterBottom customColor="var(--theme-text-primary)" content="Devices" />
            <Text variant="body2" customColor="var(--theme-text-primary)" content="Manage your connected devices and CLI instances" />
            {subscriptionInfo && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                <Chip
                  label={`${subscriptionInfo.plan.toUpperCase()} Plan`}
                  size="small"
                  sx={{
                    backgroundColor: subscriptionInfo.plan === 'pro' ? 'var(--theme-primary)' : subscriptionInfo.plan === 'enterprise' ? 'var(--theme-secondary)' : 'var(--theme-surface)',
                    color: subscriptionInfo.plan === 'pro' ? 'var(--theme-on-primary)' : subscriptionInfo.plan === 'enterprise' ? 'var(--theme-on-secondary)' : 'var(--theme-text-secondary)'
                  }}
                />
                <Chip
                  label={`${subscriptionInfo.currentDevices} / ${subscriptionInfo.deviceLimit} devices`}
                  size="small"
                  sx={{
                    backgroundColor: getUsageColor(subscriptionInfo.currentDevices, subscriptionInfo.deviceLimit) === 'error' ? 'var(--theme-error)' : getUsageColor(subscriptionInfo.currentDevices, subscriptionInfo.deviceLimit) === 'warning' ? 'var(--theme-warning)' : 'var(--theme-surface)',
                    color: getUsageColor(subscriptionInfo.currentDevices, subscriptionInfo.deviceLimit) === 'error' ? 'var(--theme-on-error)' : getUsageColor(subscriptionInfo.currentDevices, subscriptionInfo.deviceLimit) === 'warning' ? 'var(--theme-text-primary)' : 'var(--theme-text-secondary)'
                  }}
                />
              </Box>
            )}
          </Box>
          <Tooltip
            title={subscriptionInfo?.canCreateMoreDevices ? 'Register new device' : 'Device limit reached. Upgrade to register more.'}
          >
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ color: 'var(--theme-on-primary)' }} />}
                onClick={() => setRegisterDialogOpen(true)}
                disabled={!subscriptionInfo?.canCreateMoreDevices}
              >
                Register Device
              </Button>
            </span>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {subscriptionInfo && !subscriptionInfo.canCreateMoreDevices && (
          <UpgradeNotice
            message={`You've reached your device limit (${subscriptionInfo.deviceLimit} device${subscriptionInfo.deviceLimit !== 1 ? 's' : ''} allowed on ${subscriptionInfo.plan.toUpperCase()} plan).`}
          />
        )}

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              color: 'var(--theme-text-secondary)'
            },
            '& .Mui-selected': {
              color: 'var(--theme-primary)'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--theme-primary)'
            }
          }}
        >
          <Tab label={`Active (${devices.length})`} value="active" />
          <Tab label={`Revoked (${trashedDevices.length})`} value="trash" />
        </Tabs>

        {activeTab === 'active' && devices.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ComputerIcon sx={{ fontSize: 64, color: 'var(--theme-text-secondary)', mb: 2 }} />
            <Text variant="h6" gutterBottom content="No devices registered" />
            <Text customColor="var(--theme-text-secondary)" content="Register your first device to start using QwickForge" />
          </Box>
        )}

        {activeTab === 'active' && devices.length > 0 && (
          <GridLayout spacing={3}>
            {devices.map((device) => (
              <GridCell xs={12} sm={6} md={4} key={device.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ComputerIcon sx={{ color: 'var(--theme-secondary-dark)' }} />
                        <Text variant="h6" customColor='var(--theme-secondary-dark)' content={device.name} />
                      </Box>
                    </Box>

                    <Chip
                      label={getDeviceStatus(device.lastSeenAt).label}
                      size="small"
                      color={getDeviceStatus(device.lastSeenAt).color}
                      sx={{ mb: 2 }}
                    />

                    <Box sx={{ mt: 2 }}>
                      <Text variant="body2" customColor="var(--theme-text)" gutterBottom content={`Type: ${device.type}`} />
                      {device.hostname && (
                        <Text variant="body2" customColor="var(--theme-text)" gutterBottom content={`Hostname: ${device.hostname}`} />
                      )}
                      {device.platform && (
                        <Text variant="body2" customColor="var(--theme-text)" gutterBottom content={`Platform: ${device.platform}`} />
                      )}
                      <Text
                        variant="body2"
                        customColor="var(--theme-text-secondary)"
                        content={device.lastSeenAt ? `Last seen: ${new Date(device.lastSeenAt).toLocaleString()}` : 'Last seen: Never'}
                      />
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                    <Tooltip title="Revoke device access">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedDevice(device);
                          setRevokeDialogOpen(true);
                        }}
                      >
                        <BlockIcon fontSize="small" sx={{ color: 'var(--theme-error)' }} />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </GridCell>
            ))}
          </GridLayout>
        )}

        {activeTab === 'trash' && trashedDevices.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ComputerIcon sx={{ fontSize: 64, color: 'var(--theme-secondary-dark)', mb: 2 }} />
            <Text variant="h6" gutterBottom content="No revoked devices" />
            <Text customColor="var(--theme-secondary-dark)" content="Revoked devices will appear here" />
          </Box>
        )}

        {activeTab === 'trash' && trashedDevices.length > 0 && (
          <GridLayout spacing={3}>
            {trashedDevices.map((device) => (
              <GridCell xs={12} sm={6} md={4} key={device.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: 0.7, '&:hover': { opacity: 1, boxShadow: 3 } }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ComputerIcon sx={{ color: 'var(--theme-secondary-dark)', opacity: 0.5 }} />
                        <Text variant="h6" customColor='var(--theme-secondary-dark)' content={device.name} sx={{ textDecoration: 'line-through' }} />
                      </Box>
                    </Box>

                    <Chip
                      label="Revoked"
                      size="small"
                      sx={{ mb: 2, backgroundColor: 'var(--theme-error)', color: 'var(--theme-on-error)' }}
                    />

                    <Box sx={{ mt: 2 }}>
                      <Text variant="body2" customColor="var(--theme-text)" gutterBottom content={`Type: ${device.type}`} />
                      {device.hostname && (
                        <Text variant="body2" customColor="var(--theme-text)" gutterBottom content={`Hostname: ${device.hostname}`} />
                      )}
                      {device.platform && (
                        <Text variant="body2" customColor="var(--theme-text)" gutterBottom content={`Platform: ${device.platform}`} />
                      )}
                      {device.lastSeenAt && (
                        <Text variant="body2" customColor="var(--theme-text-secondary)" gutterBottom content={`Last seen: ${new Date(device.lastSeenAt).toLocaleString()}`} />
                      )}
                      {device.deletedAt && (
                        <Text variant="body2" customColor="var(--theme-text)" content={`Revoked: ${new Date(device.deletedAt).toLocaleDateString()}`} />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                    <Tooltip title="Restore device">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedDevice(device);
                          setRestoreDialogOpen(true);
                        }}
                      >
                        <RestoreIcon fontSize="small" sx={{ color: 'var(--theme-primary)' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete permanently">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedDevice(device);
                          setPermanentDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" sx={{ color: 'var(--theme-error)' }} />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </GridCell>
            ))}
          </GridLayout>
        )}

        <Dialog open={revokeDialogOpen} onClose={() => { setRevokeDialogOpen(false); setSelectedDevice(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Revoke Device Access</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Revoking this device will immediately block its API access. The device token will return 401 on all future requests. You can restore access later.
            </Alert>
            <Text
              variant="body1"
              content={`Revoke access for "${selectedDevice?.name}"?`}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setRevokeDialogOpen(false); setSelectedDevice(null); }} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRevokeDevice}
              disabled={submitting}
              sx={{
                bgcolor: 'var(--theme-error)',
                '&:hover': { bgcolor: 'var(--theme-error)' }
              }}
            >
              {submitting ? 'Revoking...' : 'Revoke Access'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Restore Device Access</DialogTitle>
          <DialogContent>
            <Text
              variant="body1"
              content={`Restore access for "${selectedDevice?.name}"? It will be moved back to active devices and its API token will work again.`}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestoreDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRestoreDevice}
              disabled={submitting}
              color="primary"
            >
              {submitting ? 'Restoring...' : 'Restore Access'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={permanentDeleteDialogOpen} onClose={() => {
          setPermanentDeleteDialogOpen(false);
          setSelectedDevice(null);
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Permanent Delete</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This action CANNOT be undone. The device and all its data will be permanently deleted.
            </Alert>
            <Text
              variant="body1"
              content={`Permanently delete "${selectedDevice?.name}"?`}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setPermanentDeleteDialogOpen(false);
              setSelectedDevice(null);
            }} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePermanentDelete}
              disabled={submitting}
              sx={{
                bgcolor: 'var(--theme-error)',
                '&:hover': { bgcolor: 'var(--theme-error)' }
              }}
            >
              {submitting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={registerDialogOpen}
          onClose={handleCloseRegisterDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Pair New Device</DialogTitle>
          <DialogContent>
            {!deviceRegistered && pairingCode && !codeExpired && (
              <Box sx={{ pt: 2 }}>
                <Text variant="body2" customColor="var(--theme-text-secondary)" content="Use this pairing code to register your device. The code expires in 10 minutes." sx={{ mb: 2 }} />

                <Card sx={{ mb: 3, bgcolor: 'var(--theme-surface)' }}>
                  <CardContent>
                    <Text variant="overline" customColor="var(--theme-text-secondary)" content="Pairing Code" />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Text variant="h3" customColor="var(--theme-text-primary)" content={pairingCode} sx={{ fontFamily: 'monospace', letterSpacing: '0.2em' }} />
                      <Button
                        size="small"
                        icon={<ContentCopyIcon sx={{ color: 'var(--theme-text-secondary)' }} />}
                        onClick={() => copyToClipboard(pairingCode)}
                      >
                        Copy
                      </Button>
                    </Box>
                    <Text variant="caption" customColor="var(--theme-text-secondary)" content={`Expires: ${new Date(expiresAt).toLocaleTimeString()}`} sx={{ mt: 1, display: 'block' }} />
                  </CardContent>
                </Card>

                <Code title="Run this command on your device:" sx={{ flex: 1, fontSize: '1rem' }}>
                  qwickai register {pairingCode}
                </Code>

                {isPolling && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
                    <CircularProgress size={20} sx={{ color: 'var(--theme-primary)' }} />
                    <Text variant="body2" customColor="var(--theme-text-secondary)" content="Waiting for device registration..." />
                  </Box>
                )}
              </Box>
            )}

            {codeExpired && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ErrorIcon sx={{ fontSize: 48, color: 'var(--theme-warning)', mb: 2 }} />
                <Text variant="h6" gutterBottom content="Pairing Code Expired" />
                <Text variant="body2" customColor="var(--theme-text-secondary)" content="The pairing code has expired. Please generate a new code." sx={{ mb: 3 }} />
                <Button variant="contained" onClick={handleRegenerateCode}>
                  Generate New Code
                </Button>
              </Box>
            )}

            {deviceRegistered && (
              <Box sx={{ pt: 2 }}>
                <Alert severity="success" icon={<CheckCircleIcon sx={{ color: 'var(--theme-success)' }} />} sx={{ mb: 3 }}>
                  Device registered successfully!
                </Alert>
                <Text variant="body2" customColor="var(--theme-text-secondary)" content="Your device is now connected and ready to use." />
              </Box>
            )}

            {!pairingCode && !error && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress sx={{ color: 'var(--theme-primary)' }} />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRegisterDialog} variant={deviceRegistered ? "contained" : "text"}>
              {deviceRegistered ? "Done" : "Cancel"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
